import { useEffect, useState } from 'react';
import { useAtom } from 'jotai';

import { fetchLegalPersonsAtAddress } from '../UserAPI';
import { authenticationAtom } from '../State';

/** Positional row shape the Companies table reads. */
export type CompanyRow = (string | undefined)[];

const toRow = (l: any): CompanyRow => [
    l.companyName, l.tradeRegisterId, l.legalForm, l.purpose,
    l.street, l.streetNumber, l.postalCode, l.city, undefined, undefined,
    l.phone, l.email, l.website, l._id,
];

const text = (value: unknown): string => (value == null ? '' : String(value).trim());

const finite = (value: unknown): value is number => Number.isFinite(value);

/**
 * Companies registered at a building. Loads once per building and address — the dependencies
 * are primitives, so a parent that re-renders with a new props object does not restart the
 * request (tri-server#1550). A response that arrives after the building changed is dropped.
 * The server needs an anchor (coordinates/bounding box or the building id) and an address part;
 * nothing is requested otherwise. A failure is logged once and reported as `failed`; it never
 * opens a modal because the panel is secondary.
 */
export function useCompaniesAtBuilding(building: any): { rows: CompanyRow[]; failed: boolean } {
    const [authentication] = useAtom(authenticationAtom) as any;
    const customer: string | undefined = authentication?.customer;

    const [rows, setRows] = useState<CompanyRow[]>([]);
    const [failed, setFailed] = useState(false);

    const buildingId = text(building?._id ?? building?.id);
    const hasBox = finite(building?.minLat) && finite(building?.maxLat)
        && finite(building?.minLon) && finite(building?.maxLon);
    const minLat = hasBox ? building.minLat : undefined;
    const maxLat = hasBox ? building.maxLat : undefined;
    const minLon = hasBox ? building.minLon : undefined;
    const maxLon = hasBox ? building.maxLon : undefined;
    const lat = hasBox ? 0.5 * (minLat + maxLat) : undefined;
    const lon = hasBox ? 0.5 * (minLon + maxLon) : undefined;
    const city = text(building?.city);
    const postalCode = text(building?.postalCode);
    const street = text(building?.street);
    const streetNumber = text(building?.streetNumber);

    // Same rule as buildingHasFlatAddress, without serialising the whole building per render.
    const hasAddress = !!(street || city || postalCode);
    const hasAnchor = hasBox || !!buildingId;

    useEffect(() => {
        setRows([]);
        setFailed(false);

        if (!customer || !hasAddress || !hasAnchor) {
            return;
        }

        let cancelled = false;

        (async () => {
            try {
                const list = await fetchLegalPersonsAtAddress(customer, {
                    lat,
                    lon,
                    minLat,
                    maxLat,
                    minLon,
                    maxLon,
                    headquarter: buildingId || undefined,
                    city,
                    postalCode,
                    street,
                    streetNumber,
                });

                if (!cancelled) {
                    setRows(list.map(toRow));
                }
            } catch (error) {
                if (!cancelled) {
                    setRows([]);
                    setFailed(true);
                    console.error('Could not load companies at building', error);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [customer, hasAddress, hasAnchor, buildingId, lat, lon, minLat, maxLat, minLon, maxLon,
        city, postalCode, street, streetNumber]);

    return { rows, failed };
}
